import { useState, useCallback, useEffect, useRef } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { BottomNav } from './BottomNav'
import { ChatPanel } from './ChatPanel'
import { AddFab } from './AddFab'
import { InstallBanner } from './InstallBanner'
import { KeyboardShortcutsHelp } from './KeyboardShortcutsHelp'
import { VoiceOverlay } from './VoiceOverlay'
import { useKeyboardShortcuts } from '@/hooks/use-keyboard-shortcuts'

export function AppShell() {
  const location = useLocation()
  const [chatOpen, setChatOpen] = useState(false)
  const [voiceStart, setVoiceStart] = useState(false)
  const [voiceOverlayActive, setVoiceOverlayActive] = useState(false)
  const [shortcutsHelpOpen, setShortcutsHelpOpen] = useState(false)
  const [navVisible, setNavVisible] = useState(true)
  const [pendingTranscription, setPendingTranscription] = useState<string | null>(null)
  const [aiButtonRect, setAiButtonRect] = useState<DOMRect | null>(null)
  const mainRef = useRef<HTMLElement>(null)
  const lastScrollTop = useRef(0)
  const lastScrollTarget = useRef<EventTarget | null>(null)

  const handleToggleShortcutsHelp = useCallback(() => {
    setShortcutsHelpOpen((prev) => !prev)
  }, [])

  useKeyboardShortcuts({ onToggleHelp: handleToggleShortcutsHelp })

  const handleAiTap = useCallback(() => {
    setVoiceStart(false)
    setChatOpen(true)
  }, [])

  const handleAiLongPress = useCallback(() => {
    // Capture the AI button's position for the fly-in animation
    const aiButton = document.querySelector('[data-ai-button]')
    if (aiButton) {
      setAiButtonRect(aiButton.getBoundingClientRect())
    }
    setVoiceOverlayActive(true)
  }, [])

  const handleVoiceOverlayTranscribed = useCallback((text: string) => {
    setPendingTranscription(text)
    setVoiceOverlayActive(false)
    setVoiceStart(false)
    setChatOpen(true)
  }, [])

  const handleVoiceOverlayClose = useCallback(() => {
    setVoiceOverlayActive(false)
  }, [])

  const handleChatClose = useCallback(() => {
    setChatOpen(false)
    setVoiceStart(false)
    setPendingTranscription(null)
  }, [])

  useEffect(() => {
    const onScroll = (event: Event) => {
      const target = event.target as Element | Document | null
      if (!target) return
      const scrollEl = target instanceof Document ? target.scrollingElement : target
      if (!scrollEl || !('scrollTop' in scrollEl)) return
      const top = (scrollEl as Element).scrollTop
      if (lastScrollTarget.current !== target) {
        lastScrollTarget.current = target
        lastScrollTop.current = top
        return
      }
      const delta = top - lastScrollTop.current
      if (top <= 24) setNavVisible(true)
      else if (delta > 8) setNavVisible(false)
      else if (delta < -8) setNavVisible(true)
      lastScrollTop.current = top
    }
    document.addEventListener('scroll', onScroll, { capture: true, passive: true })
    return () => document.removeEventListener('scroll', onScroll, { capture: true })
  }, [])

  useEffect(() => {
    // Reset nav visibility on route change — intentional cascade for immediate UX feedback
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setNavVisible(true)
    lastScrollTop.current = 0
    lastScrollTarget.current = null
  }, [location.pathname])

  return (
    <div className="flex h-dvh flex-col" style={{ paddingTop: 'var(--safe-area-top)' }}>
      <main ref={mainRef} className="flex-1 overflow-y-auto pb-20">
        <Outlet />
      </main>
      <AddFab />
      <InstallBanner />
      <ChatPanel
        open={chatOpen}
        onClose={handleChatClose}
        voiceStart={voiceStart}
        initialInput={pendingTranscription}
      />
      <VoiceOverlay
        active={voiceOverlayActive}
        onTranscribed={handleVoiceOverlayTranscribed}
        onClose={handleVoiceOverlayClose}
        originRect={aiButtonRect}
      />
      <BottomNav onAiTap={handleAiTap} onAiLongPress={handleAiLongPress} visible={navVisible} />
      <KeyboardShortcutsHelp open={shortcutsHelpOpen} onClose={() => setShortcutsHelpOpen(false)} />
    </div>
  )
}
